import {Handle, HandleCounts, HandleSettings, SideForHandle as SideForHandleBd} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {getDescriptorsOfType, InterfaceDescriptor, StringInfo, TemplateReference, NumberInfo} from "@cm/template-nodes"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {createHandle} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/create-element"
import {getParameterStorage} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/parameter-storage"
import {getTemplateLegacyId} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/asset-id"
import {GetTemplateTagsGQL} from "@app/common/components/viewers/configurator/extensions/bd-cloud/bd-cloud.generated"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {buildRemoveLayerMapping, remapLayerParameters} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/layers"
import type {ParametersParameters} from "@cm/template-nodes"

const HANDLE_UNIT_ID_PREFIX = "handle"
const HANDLE_STACK_ID = "handles-stack"
const _HANDLE_TEMPLATE_INPUT_ID = "handle" //Id of the template input to a handle stack, e.g.3e05baa6-415f-4fa1-9695-5546e55c0ebb/mattress/front-side-vertical-handles-stack/handle
const HANDLES_COUNT_SUFFIX = "handles-count"
const ACCESSORY_TYPE_ID = "accessory-type"
const HANDLE_STACK_PATTERN = /-handles-stack\/handles-stack\/handle-\d+\/handle\/accessory-type$/

const FRONT_VERTICAL_STACK = "front-side-vertical-handles-stack"
const FRONT_HORIZONTAL_STACK = "front-side-horizontal-handles-stack"
const BACK_VERTICAL_STACK = "back-side-vertical-handles-stack"
const BACK_HORIZONTAL_STACK = "back-side-horizontal-handles-stack"
const LEFT_VERTICAL_STACK = "left-side-vertical-handles-stack"
const LEFT_HORIZONTAL_STACK = "left-side-horizontal-handles-stack"
const RIGHT_VERTICAL_STACK = "right-side-vertical-handles-stack"
const RIGHT_HORIZONTAL_STACK = "right-side-horizontal-handles-stack"

type SideForHandleCm = "front" | "back" | "left" | "right"
const validSides: ReadonlySet<string> = new Set<SideForHandleCm>(["front", "back", "left", "right"])

//TODO: Rename the stack ids, then remove this
const SIDE_CM_TO_SIDE_BD: Record<SideForHandleCm, SideForHandleBd> = {
    front: "head",
    back: "foot",
    left: "left",
    right: "right",
}

const SIDE_BD_TO_SIDE_CM: Record<SideForHandleBd, SideForHandleCm> = {
    head: "front",
    foot: "back",
    left: "left",
    right: "right",
}

const STACKS_BY_SIDE: Record<SideForHandleCm, [string, string]> = {
    front: [FRONT_VERTICAL_STACK, FRONT_HORIZONTAL_STACK],
    back: [BACK_VERTICAL_STACK, BACK_HORIZONTAL_STACK],
    left: [LEFT_VERTICAL_STACK, LEFT_HORIZONTAL_STACK],
    right: [RIGHT_VERTICAL_STACK, RIGHT_HORIZONTAL_STACK],
}

function validateHandleCount(value: number, side: string): HandleCounts {
    if (value !== 0 && value !== 1 && value !== 2) {
        throw new Error(`Invalid handle count for "${side}": ${value}. Expected 0, 1, or 2.`)
    }
    return value
}

export function supportsHandles(descriptors: InterfaceDescriptor<unknown, object>[]): boolean {
    return descriptors.some((d) => d instanceof NumberInfo && d.props.id.endsWith(HANDLES_COUNT_SUFFIX))
}

export function parseSideFromHandlesCountId(id: string): SideForHandleCm | undefined {
    const match = id.match(/(\w+)-side-(?:vertical|horizontal)-handles-stack\/handles-count$/)
    if (!match) return undefined
    const side = match[1]
    if (validSides.has(side)) return side as SideForHandleCm
    return undefined
}

async function getReferenceHandle(descriptors: InterfaceDescriptor<unknown>[], context: ExtensionContext): Promise<Handle | undefined> {
    const accessoryTypeDescriptors = getDescriptorsOfType(descriptors, StringInfo).filter(
        (d) => d.props.id.endsWith(ACCESSORY_TYPE_ID) && HANDLE_STACK_PATTERN.test(d.props.id) && d.props.value === "Handle",
    )

    if (accessoryTypeDescriptors.length === 0) return undefined

    const handleBaseId = getHandleBaseId(accessoryTypeDescriptors[0].props.id)
    return createHandle(handleBaseId, descriptors, context)
}

export async function getHandleSettings(descriptors: InterfaceDescriptor<unknown, object>[], context: ExtensionContext): Promise<HandleSettings> {
    const counts: Record<SideForHandleBd, number> = {head: 0, foot: 0, left: 0, right: 0}

    for (const d of descriptors) {
        if (!(d instanceof NumberInfo)) continue
        if (!d.props.id.endsWith(HANDLES_COUNT_SUFFIX)) continue

        const side = parseSideFromHandlesCountId(d.props.id)
        if (!side) continue

        counts[SIDE_CM_TO_SIDE_BD[side]] = Math.max(counts[SIDE_CM_TO_SIDE_BD[side]], d.props.value ?? 0)
    }

    const reference = await getReferenceHandle(descriptors, context)
    return {
        head: validateHandleCount(counts.head, "head"),
        foot: validateHandleCount(counts.foot, "foot"),
        left: validateHandleCount(counts.left, "left"),
        right: validateHandleCount(counts.right, "right"),
        reference,
    }
}

// Extracts the handle base id up to and including the "handle-N" segment.
// e.g. "3e05baa6-.../mattress/front-side-vertical-handles-stack/handles-stack/handle-0/handle/accessory-type"
//    → "3e05baa6-.../mattress/front-side-vertical-handles-stack/handles-stack/handle-0"
export function getHandleBaseId(descriptorId: string): string {
    const match = descriptorId.match(new RegExp(`^(.*/${HANDLE_UNIT_ID_PREFIX}-\\d+)`))
    if (!match) throw new Error(`No handle segment found in "${descriptorId}"`)
    return match[1]
}

export function getHandleStackId(descriptorId: string): string {
    const match = descriptorId.match(new RegExp(`^(.*)/${HANDLE_STACK_ID}`))
    if (!match) throw new Error(`No handles-stack segment found in "${descriptorId}"`)
    return match[1]
}

export function getHandleTemplateInput(handleDescriptor: string): string {
    const match = handleDescriptor.match(/^(.*\/)\w+-side-(vertical|horizontal)-handles-stack\//)
    if (!match) throw new Error(`No handle stack segment found in "${handleDescriptor}"`)
    return match[1] + match[2] + "-handle"
}

/* Returns parameter storage entries for the stacks identified by verticalStack and horizontalStack, that refer to 
actual handles. I.e. the handle-count is excluded.
 e.g. keys containing "front-side-vertical-handles-stack/handles-stack/handle-0..." or "front-side-horizontal-handles-stack/handles-stack/handle-0..."*/
export function getHandleStackParameters(params: ParametersParameters, verticalStackId: string, horizontalStackId: string): ParametersParameters {
    const verticalPrefix = `${verticalStackId}/${HANDLE_STACK_ID}`
    const horizontalPrefix = `${horizontalStackId}/${HANDLE_STACK_ID}`
    const result: ParametersParameters = {}
    for (const [key, value] of Object.entries(params)) {
        if (key.includes(verticalPrefix) || key.includes(horizontalPrefix)) {
            result[key] = value
        }
    }
    return result
}

/*Replicates handle parameters from a source handle to all target stacks for indices fromIndex..count-1.
 Handles below fromIndex already exist and keep their parameters, e.g. an individually set material.
 sourceHandleBaseKey is the unwrapped id from a reference handle (e.g. "mattress/front-side-vertical-handles-stack/handles-stack/handle-0"). All keys of the parameter storage that  with this prefix are values assigned to the handle. Those should be
 applied to the target stacks. 
 targetHandleStackBases are the full handle stack bases (e.g. "mattress/back-side-vertical-handles-stack/handles-stack").
 We construct ids for handles in those stacks and assign the parameters of the reference handle. */
export function replicateHandleParams(
    params: ParametersParameters,
    sourceHandleBaseKey: string,
    targetHandleStackBases: string[],
    fromIndex: number,
    count: number,
): ParametersParameters {
    const sourcePrefix = sourceHandleBaseKey + "/"
    const suffixEntries: {suffix: string; value: ParametersParameters[string]}[] = []

    for (const [key, value] of Object.entries(params)) {
        if (key.startsWith(sourcePrefix)) {
            suffixEntries.push({suffix: key.slice(sourcePrefix.length), value})
        }
    }

    if (suffixEntries.length === 0) return {}

    const result: ParametersParameters = {}
    for (const stackBase of targetHandleStackBases) {
        for (let i = fromIndex; i < count; i++) {
            for (const {suffix, value} of suffixEntries) {
                result[`${stackBase}/${HANDLE_UNIT_ID_PREFIX}-${i}/${suffix}`] = value
            }
        }
    }
    return result
}

export function setHandleCount(
    side: SideForHandleBd,
    newCount: HandleCounts,
    currentCount: HandleCounts,
    referenceHandle: Handle | undefined,
    context: ExtensionContext,
): void {
    const cmSide = SIDE_BD_TO_SIDE_CM[side]
    const [verticalStack, horizontalStack] = STACKS_BY_SIDE[cmSide]
    const verticalSuffix = `${verticalStack}/${HANDLES_COUNT_SUFFIX}`
    const horizontalSuffix = `${horizontalStack}/${HANDLES_COUNT_SUFFIX}`

    const descriptors = context.sceneManagerService.$descriptorList()
    const matchingIds: string[] = []
    for (const d of descriptors) {
        if (!(d instanceof NumberInfo)) continue
        if (d.props.id.endsWith(verticalSuffix) || d.props.id.endsWith(horizontalSuffix)) {
            matchingIds.push(d.props.id)
        }
    }

    if (matchingIds.length === 0) return

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        const countKeyEntries = matchingIds.map((id) => getParameterStorage(templateGraph, id))
        const parametersNode = countKeyEntries[0].storingInstance.parameters.parameters
        const countUpdates: ParametersParameters = {}
        for (const {key} of countKeyEntries) {
            countUpdates[key] = newCount
        }

        if (newCount < currentCount) {
            const currentParams = parametersNode.parameters
            const handleParams = getHandleStackParameters(currentParams, verticalStack, horizontalStack)

            let remapped = handleParams
            for (let count = currentCount; count > newCount; count--) {
                const mapping = buildRemoveLayerMapping(count, count - 1)
                remapped = remapLayerParameters(remapped, HANDLE_UNIT_ID_PREFIX, mapping)
            }

            const newParams: ParametersParameters = {}
            for (const [key, value] of Object.entries(currentParams)) {
                if (!(key in handleParams)) {
                    newParams[key] = value
                }
            }
            Object.assign(newParams, remapped, countUpdates)
            parametersNode.replaceParameters(newParams)
        } else if (newCount > currentCount && referenceHandle && referenceHandle.materialInputs.length > 0) {
            const handleBaseId = getHandleBaseId(referenceHandle.materialInputs[0].id)
            const {key: sourceBaseKey} = getParameterStorage(templateGraph, handleBaseId)

            const targetHandleStackBases = [...new Set(countKeyEntries.map(({key}) => key.slice(0, -HANDLES_COUNT_SUFFIX.length) + HANDLE_STACK_ID))]

            const replicatedParams = replicateHandleParams(parametersNode.parameters, sourceBaseKey, targetHandleStackBases, currentCount, newCount)
            parametersNode.updateParameters({...replicatedParams, ...countUpdates})
        } else {
            parametersNode.updateParameters(countUpdates)
        }
    })
}

export function removeHandle(context: ExtensionContext): void {
    const descriptors = context.sceneManagerService.$descriptorList()
    const verticalDescriptor = descriptors.find((d) => d.props.id.endsWith(VERTICAL_HANDLE_SUFFIX))
    const horizontalDescriptor = descriptors.find((d) => d.props.id.endsWith(HORIZONTAL_HANDLE_SUFFIX))

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        for (const descriptor of [verticalDescriptor, horizontalDescriptor]) {
            if (!descriptor) continue
            const {storingInstance, key} = getParameterStorage(templateGraph, descriptor.props.id)
            const params = {...storingInstance.parameters.parameters.parameters}
            delete params[key]
            storingInstance.parameters.parameters.replaceParameters(params)
        }
    })
}

type HandleOrientation = "vertical" | "horizontal"

const VERTICAL_HANDLE_SUFFIX = "mattress/vertical-handle"
const HORIZONTAL_HANDLE_SUFFIX = "mattress/horizontal-handle"

async function getHandleOrientation(templateId: string, context: ExtensionContext): Promise<HandleOrientation> {
    const query = context.injector.get(GetTemplateTagsGQL)
    const result = await fetchThrowingErrors(query)({templateId})
    const tags = result?.configuratorData?.tagNames ?? []
    if (tags.includes("Vertical")) return "vertical"
    if (tags.includes("Horizontal")) return "horizontal"
    throw new Error(`Template ${templateId} has no "Vertical" or "Horizontal" tag`)
}

export async function setHandle(handleId: string, context: ExtensionContext): Promise<void> {
    const [orientation, legacyId] = await Promise.all([getHandleOrientation(handleId, context), getTemplateLegacyId(handleId, context)])

    const descriptors = context.sceneManagerService.$descriptorList()
    const targetSuffix = orientation === "vertical" ? VERTICAL_HANDLE_SUFFIX : HORIZONTAL_HANDLE_SUFFIX
    const clearSuffix = orientation === "vertical" ? HORIZONTAL_HANDLE_SUFFIX : VERTICAL_HANDLE_SUFFIX

    const targetDescriptor = descriptors.find((d) => d.props.id.endsWith(targetSuffix))
    const clearDescriptor = descriptors.find((d) => d.props.id.endsWith(clearSuffix))

    if (!targetDescriptor) throw new Error(`No descriptor found ending with "${targetSuffix}"`)

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        const {storingInstance, key} = getParameterStorage(templateGraph, targetDescriptor.props.id)
        storingInstance.parameters.parameters.updateParameters({
            [key]: new TemplateReference({parameters: {templateId: legacyId, templateRevisionId: null}}),
        })

        if (clearDescriptor) {
            const {storingInstance: clearInstance, key: clearKey} = getParameterStorage(templateGraph, clearDescriptor.props.id)
            const params = {...clearInstance.parameters.parameters.parameters}
            delete params[clearKey]
            clearInstance.parameters.parameters.replaceParameters(params)
        }
    })
}

// Returns the handles-count descriptor id for the stack that contains the given handle input.
// e.g. "3e05baa6-.../mattress/front-side-vertical-handles-stack/handles-stack/handle-0/handle/material"
//    → "3e05baa6-.../mattress/front-side-vertical-handles-stack/handles-count"
export function getCountInputId(descriptorId: string): string {
    const match = descriptorId.match(/^(.*-handles-stack)\//)
    if (!match) throw new Error(`No handles-stack segment found in "${descriptorId}"`)
    return `${match[1]}/${HANDLES_COUNT_SUFFIX}`
}

// Extracts the suffix after the "handles-stack/handle-N/" segment.
// e.g. "3e05baa6-.../mattress/front-side-vertical-handles-stack/handles-stack/handle-0/handle/material"
//    → "handle/material"
export function getHandleInputSuffix(descriptorId: string): string {
    const match = descriptorId.match(/handles-stack\/handle-\d+\/(.+)$/)
    if (!match) throw new Error(`No handle input suffix found in "${descriptorId}"`)
    return match[1]
}

/*Gets all related inputs across all handle stacks, i.e. for:
descriptorId === 3e05baa6-415f-4fa1-9695-5546e55c0ebb/mattress/front-side-vertical-handles-stack/handles-stack/handle-0/handle/material

the result is
3e05baa6-415f-4fa1-9695-5546e55c0ebb/mattress/X/handles-stack/handle-Y/handle/material
with X/Y being all currently available values (includes the original input)
*/
export function getRelatedInputs(descriptorId: string, descriptors: InterfaceDescriptor<unknown, object>[]): string[] {
    const suffix = getHandleInputSuffix(descriptorId)
    const result: string[] = []

    for (const d of descriptors) {
        if (!(d instanceof NumberInfo)) continue
        if (!d.props.id.endsWith(HANDLES_COUNT_SUFFIX)) continue

        const count = d.props.value ?? 0
        const stackBase = d.props.id.slice(0, -HANDLES_COUNT_SUFFIX.length)
        for (let i = 0; i < count; i++) {
            result.push(`${stackBase}${HANDLE_STACK_ID}/${HANDLE_UNIT_ID_PREFIX}-${i}/${suffix}`)
        }
    }

    return result
}
